import React from 'react';
import classnames from 'classnames/bind';
import { FormProvider } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';

import { organizationPlansPath } from 'application/types/routes';
import { Deployment } from 'types/deployment';
import Pipeline from 'types/pipeline';
import { Gateway } from 'types/gateway';

import { VideoSource } from 'hooks/api/useVideoSources';
import { useAPI } from 'hooks/api/useAPI';
import { useCameras } from 'cameras/hooks/useCameras';
import { useCreateDeployment } from 'deployments/hooks/useCreateDeployment';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useFileStreams } from 'files/hooks/useFileStreams';
import { useGateways } from 'gateways/hooks/useGateways';
import { useGateway } from 'gateways/hooks/useGateway';
import { useMqttGatewaysState } from 'gateways/hooks/useMqttGatewaysState';
import { useStartCloudGateway } from 'gateways/hooks/useStartCloudGateway';
import { useInputStreams } from 'streams/hooks/useInputStreams';
import { usePipeline } from 'pipelines/hooks/usePipeline';
import { usePipelines } from 'pipelines/hooks/usePipelines';
import { useUsageStatistics } from 'organizations/hooks/useUsageStatistics';

import { ActivePipelineContext } from 'pipelines/context/active_pipeline';

import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { PipelinePicker } from 'pipelines/components/PipelinePicker/PipelinePicker';
import { Separator } from 'components/Separator/Separator';
import { Text } from 'components/Text/Text';

import { CannotDeployInfo } from '../DeployabilityModal/CannotDeployInfo';
import { DeploymentFormActions } from './Actions';
import { DeploymentFormContent, DeploymentFormContentRef } from './Content';
import { DeploymentFormErrorMessage } from './DeploymentFormErrorMessage';
import { DeploymentFormProvider } from './context';
import { useDeploymentFormContent } from './useDeploymentFormContent';
import styles from './Form.module.scss';

const c = classnames.bind(styles);

export type NewDeploymentFormProps = {
  nameTemplate?: string;
  className?: string;
  pipelineId?: Pipeline['id'];
  gateway?: Gateway | null;
  allowedStreamIds?: string[];
  allowedCameraIds?: string[];
  initialDeployment?: Deployment;
  onSuccess?: (deployment: Deployment) => void;
  onLink?: () => void;
  canStartOnDeploy?: boolean;
};

function NewDeploymentForm({
  nameTemplate,
  className,
  pipelineId: initialPipelineId,
  gateway: initialGateway = null,
  allowedStreamIds,
  allowedCameraIds,
  initialDeployment,
  onSuccess,
  onLink,
  canStartOnDeploy = true,
}: NewDeploymentFormProps) {
  const location = useLocation();

  const { applicationID } = useAPI();
  const { data: organization } = useCurrentOrganization();

  const { data: pipelines, isLoading: isLoadingPipelines } = usePipelines();
  const { data: gateways, isLoading: isLoadingGateways } = useGateways();
  const { data: cameras, isLoading: isLoadingCameras } = useCameras();
  const { data: inputStreams, isLoading: isLoadingInputStreams } =
    useInputStreams();
  const { data: fileStreams, isLoading: isLoadingFileStreams } =
    useFileStreams();

  const isLoadingCanDeploy =
    isLoadingPipelines ||
    isLoadingGateways ||
    isLoadingCameras ||
    isLoadingInputStreams ||
    isLoadingFileStreams;

  const hasPipelines = Boolean(pipelines?.total_elements);
  const hasGateways = Boolean(gateways?.total_elements);
  const hasCameras = Boolean(cameras?.total_elements);
  const hasInputStreams = Boolean(inputStreams?.total_elements);
  const hasFileStreams = Boolean(fileStreams?.total_elements);
  const hasInputSources = hasCameras || hasInputStreams || hasFileStreams;
  const canDeploy = hasGateways && hasInputSources;

  const { isLoading: isLoadingStatistics, data: usageStatistics } =
    useUsageStatistics();
  const { isLoading: isLoadingPlan, data: currentPlan } = useCurrentPlan();

  const [pipelineId, setPipelineId] = React.useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('pipeline_id') ?? initialPipelineId;
  });

  const { data: pipeline, isLoading: isLoadingPipeline } =
    usePipeline(pipelineId);

  const [pipelineError, setPipelineError] = React.useState<string>();
  const [gatewayId, setGatewayId] = React.useState(initialGateway?.id);
  const [gatewayError, setGatewayError] = React.useState<string>();

  // Fetch gateway data from React Query (will be updated by MQTT)
  const { data: gateway } = useGateway(gatewayId);

  // Subscribe to MQTT updates for all gateways (selected + list)
  const gatewayIdsForMqtt = React.useMemo(() => {
    const ids = [];

    // Add selected gateway ID
    if (gatewayId) {
      ids.push(gatewayId);
    }

    // Add all gateway IDs from the list for real-time status in dropdown
    if (gateways?.data) {
      ids.push(...gateways.data.map((g) => g.id));
    }

    // Remove duplicates
    return [...new Set(ids)];
  }, [gatewayId, gateways?.data]);

  useMqttGatewaysState(gatewayIdsForMqtt);

  const [shouldStartImmediately, toggleShouldStartImmediately] =
    React.useState(canStartOnDeploy);

  // Handler to update gateway selection
  const handleGatewayChange = React.useCallback((gateway: Gateway | null) => {
    setGatewayId(gateway?.id);
  }, []);

  // Auto-start and preselect Lumeo Cloud OnDemand gateway if present
  const { mutate: startCloudGateway, isLoading: isStartingCloudGateway } =
    useStartCloudGateway();

  const { data: ondemandGatewaysData } = useGateways({
    params: { gateway_models: ['Lumeo Cloud OnDemand'], limit: 1 },
    enabled: !gateway && !initialGateway,
    queryKey: ['gateways-ondemand-autoselect'],
  });

  // Auto-select and start OnDemand gateway if present (takes priority)
  // Otherwise, auto-select if there's only one gateway available
  React.useEffect(() => {
    if (!gateway && !initialGateway && !isStartingCloudGateway) {
      // Prioritize OnDemand gateway
      if (ondemandGatewaysData?.data && ondemandGatewaysData.data.length > 0) {
        const [ondemand] = ondemandGatewaysData.data;
        setGatewayId(ondemand.id);
        // Only start if gateway is offline or pending (not already online/starting)
        if (ondemand.status !== 'online' && ondemand.status !== 'starting') {
          startCloudGateway(ondemand.id);
        }
      } else if (gateways?.data && gateways.data.length === 1) {
        // Fall back to single gateway if no OnDemand gateway
        const [singleGateway] = gateways.data;
        setGatewayId(singleGateway.id);
      }
    }
  }, [
    gateway,
    initialGateway,
    ondemandGatewaysData,
    gateways,
    startCloudGateway,
    isStartingCloudGateway,
  ]);

  const formContentRef = React.useRef<DeploymentFormContentRef>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const {
    mutate: createDeployment,
    error,
    isLoading,
  } = useCreateDeployment({
    onError: () => containerRef.current?.scrollIntoView(),
    onSuccess,
  });

  async function handleSubmit() {
    setGatewayError(undefined);
    setPipelineError(undefined);

    if (!formContentRef.current || !applicationID) {
      return;
    }

    if (!gateway) {
      setGatewayError('Please select a gateway.');
      return;
    }

    if (!pipeline) {
      setPipelineError('Please select a workflow.');
      return;
    }

    try {
      const { configuration } = await formContentRef.current.onSubmit();

      const deploymentName = nameTemplate
        ? nameTemplate.replace('{{pipeline_name}}', pipeline.name)
        : null;

      createDeployment({
        name: deploymentName,
        pipeline_id: pipeline.id,
        gateway_id: gateway.id,
        state: shouldStartImmediately ? 'running' : 'stopped',
        configuration,
      });
    } catch {
      // Do nothing. Errors are displayed inline.
    }
  }

  function handlePipelineSelect(pipeline: Pipeline) {
    setPipelineId(pipeline.id);
    setPipelineError(undefined);
  }

  if (isLoadingCanDeploy || isLoadingPlan || isLoadingStatistics) {
    return <>Loading...</>;
  }

  if (!canDeploy) {
    return (
      <CannotDeployInfo
        hasGateways={hasGateways}
        hasInputSources={hasInputSources}
        hasPipelines={hasPipelines}
      />
    );
  }

  const isExceedingDeploymentCount =
    usageStatistics &&
    currentPlan?.running_deployment_limit &&
    usageStatistics.deployment_count >= currentPlan.running_deployment_limit;

  if (isExceedingDeploymentCount) {
    return (
      <div className={c('form-warning')}>
        <Icon
          className={c('form-warning-icon', 'theme warning')}
          size="large"
          name="warning"
        />
        <Heading level="3">
          Please stop a deployment or{' '}
          <Link
            className="link"
            to={organizationPlansPath(applicationID, organization?.id)}
            onClick={() => onLink?.()}
          >
            upgrade your plan
          </Link>{' '}
          before starting another deployment.
        </Heading>
        <Text>
          Your plan is limited to {currentPlan.running_deployment_limit}{' '}
          concurrent deployments.
        </Text>
      </div>
    );
  }

  return (
    <DeploymentFormProvider
      pipeline={pipeline}
      gateway={gateway}
      allowedStreamIds={allowedStreamIds}
      allowedCameraIds={allowedCameraIds}
    >
      <div
        id="up-new-deployment"
        className={c('container', className)}
        ref={containerRef}
      >
        {!initialPipelineId && (
          <section className={c('header')}>
            {pipelineId && isLoadingPipeline && !pipeline ? (
              <span className="skeleton-bar" />
            ) : (
              <div className={c({ error: pipelineError })}>
                <PipelinePicker
                  pipeline={pipeline}
                  hasPipelines={hasPipelines}
                  onPipelineSelect={handlePipelineSelect}
                />
              </div>
            )}

            {pipelineError && <Text intent="danger">{pipelineError}</Text>}

            <Separator />
          </section>
        )}

        <section id="up-new-deployment_content" className={c('body')}>
          {!pipeline ? (
            <Text>Select a workflow you want to deploy.</Text>
          ) : (
            <div
              className={c('field')}
              aria-label="Configure deployment parameters"
            >
              <ActivePipelineContext.Provider value={pipeline}>
                {error && <DeploymentFormErrorMessage error={error} />}

                <FormContent deployment={initialDeployment}>
                  <DeploymentFormContent
                    key={`${pipeline.id}_content`}
                    ref={formContentRef}
                    onLink={onLink}
                  />
                </FormContent>
              </ActivePipelineContext.Provider>
            </div>
          )}
        </section>
        <DeploymentFormActions
          pipeline={pipeline}
          startImmediately={shouldStartImmediately}
          onToggleStartImmediately={toggleShouldStartImmediately}
          gateway={gateway}
          gatewayError={gatewayError}
          onGatewayChange={handleGatewayChange}
          isDeploying={isLoading}
          onSubmit={handleSubmit}
          canStartOnDeploy={canStartOnDeploy}
        />
      </div>
    </DeploymentFormProvider>
  );
}

function areEqual(prev: NewDeploymentFormProps, next: NewDeploymentFormProps) {
  return prev.pipelineId === next.pipelineId;
}

const Memoized = React.memo(NewDeploymentForm, areEqual);

export { Memoized as NewDeploymentForm };

type FormContentProps = React.PropsWithChildren<{
  deployment?: Deployment;
  videoSource?: VideoSource;
}>;

function FormContent({ deployment, videoSource, children }: FormContentProps) {
  const formMethods = useDeploymentFormContent(deployment, videoSource);
  return <FormProvider {...formMethods}>{children}</FormProvider>;
}
